import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getMapboxToken, mapboxAutocomplete, type MapboxSuggestion } from "@/lib/mapboxGeocoder";

interface GooglePlacesAddressProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  onPlaceSelect?: (place: any) => void;
  onCoordinatesChange?: (coords: { lat: number; lng: number } | null) => void;
}

export const GooglePlacesAddress = ({
  value,
  onChange,
  placeholder = "Enter full address",
  className,
  id,
  name,
  required,
  onPlaceSelect,
  onCoordinatesChange,
}: GooglePlacesAddressProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<number | null>(null);

  const [suggestions, setSuggestions] = useState<MapboxSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPlaceSelectRef.current = onPlaceSelect; }, [onPlaceSelect]);
  useEffect(() => { onCoordinatesChangeRef.current = onCoordinatesChange; }, [onCoordinatesChange]);

  // Pre-warm the Mapbox token
  useEffect(() => {
    getMapboxToken().catch((err) => console.error("Mapbox token load failed:", err));
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    try {
      const results = await mapboxAutocomplete(input, {
        types: "address,poi,place,locality,postcode",
        limit: 6,
      });
      setSuggestions(results);
      setOpen(results.length > 0);
      setHighlight(0);
    } catch (err) {
      console.error("Mapbox autocomplete failed:", err);
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => fetchSuggestions(v), 250);
  };

  const selectSuggestion = (s: MapboxSuggestion) => {
    onChange(s.fullText);
    setOpen(false);
    setSuggestions([]);
    onCoordinatesChangeRef.current?.(s.coords);
    onPlaceSelectRef.current?.({
      formatted_address: s.fullText,
      geometry: s.coords
        ? { location: { lat: () => s.coords!.lat, lng: () => s.coords!.lng } }
        : undefined,
      name: s.primary,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectSuggestion(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={cn(className)}
        id={id}
        name={name}
        required={required}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-lg">
          {suggestions.map((s, i) => (
            <li
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm",
                i === highlight && "bg-accent text-accent-foreground"
              )}
            >
              <div className="font-medium">{s.primary}</div>
              {s.secondary && <div className="text-xs text-muted-foreground">{s.secondary}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
